import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import TextInput from '../inputs/TextInput';
import DateInput from '../inputs/DateInput';
import Dropdown from '../inputs/Dropdown';
import CheckboxGroupScrollable from '../inputs/CheckboxGroupScrollable';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { FaTimes } from "react-icons/fa";
import { postNewEvent, postNewEventWithRecurrence } from '../../api/admin/AdminDashboardAPI';
import { DateTimeFormatter, LocalDateTime } from '@js-joda/core';
import toast from 'react-hot-toast';
import { useAdmin } from '../../pages/admin/AdminContext';
import RecurrenceSelector from '../inputs/RecurrenceSelector';
import { generateRecurrenceSchedule } from '../../api/admin/RecurrenceFormatter';

Modal.setAppElement('#root');

const EventCreateModal = ({ isOpen, onClose, initialFormData, onFormDataChange, addNewEvent, eventTypes }) => {
    const { committees } = useAdmin();


    const [eventName, setEventName] = useState(initialFormData?.eventName || "");
    const [eventStartDateTime, setEventStartDateTime] = useState(initialFormData?.startDate || "");
    const [eventLocation, setEventLocation] = useState(initialFormData?.location || "");
    const [eventEndDateTime, setEventEndDateTime] = useState(initialFormData?.endDate || "");
    const [selectedCommittee, setSelectedCommittee] = useState(initialFormData?.committee || "None");
    const [selectedRecurrence, setSelectedRecurrence] = useState(initialFormData?.recurrence?.frequency || "None");
    const [selectedEventTypeIDs, setSelectedEventTypeIDs] = useState(() =>
        initialFormData?.selectedEventTypeIDs
            ? initialFormData.selectedEventTypeIDs.map(id => Number(id))
            : []
    );

    const [recurrOption, setRecurrOption] = useState(null);

    const didInit = useRef(false);

    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    useEffect(() => {
        if (isOpen) {
            if (!didInit.current) {
                setEventName(initialFormData?.name || "");
                setEventStartDateTime(initialFormData?.startDate || "");
                setEventLocation(initialFormData?.location || "");
                setEventEndDateTime(initialFormData?.endDate || "");
                setSelectedCommittee(initialFormData?.committee || "None");
                setSelectedRecurrence(initialFormData?.recurrence?.frequency || "None");
                setSelectedEventTypeIDs(
                    initialFormData?.selectedEventTypeIDs
                        ? initialFormData.selectedEventTypeIDs.map(id => Number(id))
                        : []
                );
                didInit.current = true;
            }
        } else {
            didInit.current = false;
        }
    }, [isOpen, initialFormData]);

    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current({
                name: eventName,
                startDate: eventStartDateTime,
                location: eventLocation,
                endDate: eventEndDateTime,
                committee: selectedCommittee,
                recurrence: selectedRecurrence,
                selectedEventTypeIDs
            });
        }
    }, [eventName, eventStartDateTime, eventLocation, eventEndDateTime, selectedCommittee, selectedRecurrence, selectedEventTypeIDs]);

    const handleEventTypeSelect = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        if (isChecked) {
            setSelectedEventTypeIDs(prev => [...prev, changedValue]);
        } else {
            setSelectedEventTypeIDs(prev => prev.filter((id) => id !== changedValue));
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="eventTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedEventTypeIDs.includes(value);
            });
        });
    }, [selectedEventTypeIDs]);

    const committeeOptions = React.useMemo(() => [
        { value: 'None', label: 'None' },
        ...committees.map((committee) => ({
            value: committee.committeeName,
            label: committee.committeeName
        }))
    ], [committees]);

    const handleCommitteeChange = (e) => {
        setSelectedCommittee(e.target.value);
    };

    // TODO: Need to work on logic on this
    const recurrenceOptions = [
        { value: 'None', label: 'No Recurrence' },
        { value: 'DAILY', label: 'Daily' },
        { value: 'WEEKLY', label: 'Weekly' },
        { value: 'MONTHLY', label: 'Monthly' },
    ];
    const handleRecurrenceChange = (e) => {
        setSelectedRecurrence(e.target.value);
    };

    // TODO: reset on submit, when clicking out, have are you sure or smthn (or don't reset when closed out)
    const handleReset = () => {
        setEventName("");
        setEventStartDateTime("");
        setEventLocation("");
        setEventEndDateTime("");
        setSelectedCommittee("None");
        setSelectedRecurrence("None");
        setSelectedEventTypeIDs([]);
    };

    const isRecurrenceValid = () => {
        if (selectedRecurrence === "None") return true;
        if (!recurrOption?.endDate) return false;
        switch (selectedRecurrence) {
            case "DAILY":
                return true;
            case "WEEKLY":
                return Array.isArray(recurrOption.weekDays) &&
                    recurrOption.weekDays.length > 0;
            case "MONTHLY":
                return Number.isInteger(recurrOption.dateOfMonth);
            default:
                return false;
        }
    };

    const isFormValid = () => {
        if (!eventName || eventName.trim() === "") {
            return false;
        }
        if (!eventStartDateTime) {
            return false;
        }
        if (!eventLocation || eventLocation.trim() === "") {
            return false;
        }
        if (!eventEndDateTime) {
            return false;
        }
        if (selectedCommittee === "None") {
            return false;
        }
        if (!selectedRecurrence) {
            return false;
        }
        if (!selectedEventTypeIDs || selectedEventTypeIDs.length === 0) {
            return false;
        }
        if (!isRecurrenceValid()) { return false; }
        return true;
    };

    const submitEvent = async () => {
        if (selectedRecurrence !== "None") {
            if (!recurrOption?.endDate) {
                toast.error("Please select an end date for the recurrence.");
                return;
            }
            const eventEndDay = eventEndDateTime.slice(0, 10);
            if (recurrOption.endDate <= eventEndDay) {
                toast.error("Recurrence end date cannot be before the event end date.");
                return;
            }
            if (selectedRecurrence === "WEEKLY" &&
                (!Array.isArray(recurrOption.weekDays) || !recurrOption.weekDays.length)) {
                toast.error("Select at least one weekday for a weekly recurrence.");
                return;
            }
            if (selectedRecurrence === "MONTHLY" &&
                !Number.isInteger(recurrOption.dateOfMonth)) {
                toast.error("Please pick a day of the month for a monthly recurrence.");
                return;
            }
        }

        if (!isFormValid()) {
            toast.error("All form details must be filled");
            return;
        }
        const isCorrectDate = LocalDateTime.parse(eventStartDateTime, DateTimeFormatter.ISO_DATE_TIME)
            .isBefore(LocalDateTime.parse(eventEndDateTime, DateTimeFormatter.ISO_DATE_TIME));

        if (!isCorrectDate) {
            toast.error("Start Date & Time must be\n before End Date & Time.");
            return;
        }

        if (eventName.length > 80) {
            toast.error("Event Name is too long");
            return;
        }

        try {
            toast.loading("Creating Event...");
            let jsonBody;
            let response;
            if (selectedRecurrence !== "None") {
                recurrOption.startDate = eventStartDateTime;
                const recurrDates = generateRecurrenceSchedule(recurrOption).occurrences;
                jsonBody = {
                    event: {
                        eventName: eventName,
                        startDateTime: eventStartDateTime,
                        endDateTime: eventEndDateTime,
                        location: eventLocation,
                        committeeName: selectedCommittee,
                        eventTypeIds: selectedEventTypeIDs
                    },
                    recurrence: recurrOption,
                    dates: recurrDates
                };

                console.log(jsonBody);
                response = await postNewEventWithRecurrence(jsonBody);
            } else {
                jsonBody = {
                    eventName: eventName,
                    startDateTime: eventStartDateTime,
                    endDateTime: eventEndDateTime,
                    location: eventLocation,
                    committeeName: selectedCommittee,
                    eventTypeIds: selectedEventTypeIDs
                }

                response = await postNewEvent(jsonBody);
            }

            setTimeout(async () => {
                if (response.ok) {
                    toast.dismiss();
                    try {
                        const body = await response.json();
                        toast.success("Event created!");
                        if (selectedRecurrence === "None") {
                            addNewEvent(body);
                        } else {
                            body.forEach((e) => addNewEvent(e))
                        }
                        didInit.current = false;
                        onClose();
                    } catch (jsonError) {
                        console.error("Error parsing JSON:", jsonError);
                        toast.error("Error parsing server response.");
                    }
                } else {
                    toast.dismiss();
                    console.log("Error creating event. Status:", response.status);
                    toast.error("Event failed to create. Status: " + response.status);
                }

            }, 500);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting event:", error);
            alert("Something went wrong. Please check your connection and try again.");
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Event Create Modal"
            className="custom-modal-content member-filter-modal"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className="d-flex flex-row justify-content-between align-items-center mb-3">
                    <h2 className="text-start mb-0">Create Event</h2>
                    <div className="d-flex flex-row justify-content-end align-items-center me-1">
                        <button className="close-btn" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>

                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <TextInput
                            id="eventNameCreate"
                            placeholder="Event Name"
                            className="form-input"
                            charLimit={200}
                            label="Event Name"
                            value={eventName}
                            setValue={(e) => setEventName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <TextInput
                            id="eventLocationCreate"
                            placeholder="Location"
                            className="form-input"
                            charLimit={300}
                            label="Location"
                            value={eventLocation}
                            setValue={(e) => setEventLocation(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <DateInput
                            id="eventStartDateCreate"
                            className="form-input"
                            label="Start Date & Time"
                            value={eventStartDateTime}
                            setValue={(e) => {
                                setEventStartDateTime(e.target.value)
                            }
                            }
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <Dropdown
                            id="committee-dropdown"
                            name="committees"
                            options={committeeOptions}
                            onChange={handleCommitteeChange}
                            className="form-input"
                            label="Committee"
                            value={selectedCommittee}
                            required
                        />
                    </div>
                </div>

                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <DateInput
                            id="eventEndDateCreate"
                            className="form-input"
                            label="End Date & Time"
                            value={eventEndDateTime}
                            setValue={(e) => setEventEndDateTime(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <Dropdown
                            id="recurrence-dropdown"
                            name="recurrence"
                            options={recurrenceOptions}
                            onChange={handleRecurrenceChange}
                            className="form-input"
                            label="Recurrence"
                            value={selectedRecurrence}
                            required
                        />
                    </div>
                </div>

                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <CheckboxGroupScrollable
                            id="event-types"
                            name="eventTypesGroup"
                            label="Event Type(s)"
                            options={eventTypes.map((et) => ({
                                value: et.id,
                                label: et.eventType,
                            }))}
                            taller={true}
                            selectedValues={selectedEventTypeIDs}
                            onChange={handleEventTypeSelect}
                            onSelectAll={() => setSelectedEventTypeIDs(eventTypes.map((et) => et.id))}
                            onUnselectAll={() => setSelectedEventTypeIDs([])}
                        />
                    </div>

                    <div className="form-field w-50">
                        <RecurrenceSelector
                            selectedRecurrence={selectedRecurrence}
                            initialValue={null}
                            onChange={(c) => { if (c) setRecurrOption(c); }}
                        />
                    </div>

                </div>

                <div className="d-flex flex-row justify-content-center align-items-center two-button-container mt-3">
                    <div className="me-3">
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Create"
                        action={submitEvent}
                    />
                </div>
            </>
        </Modal>
    );
};

EventCreateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    addNewEvent: PropTypes.func.isRequired,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
};

export default EventCreateModal;
